// 📺 Cartelería para TV (kiosko público por token). Ruta /tv/:token — sin login,
// pantalla completa, estilo Netflix. Reproduce ESCENAS de la plantilla (programada
// por horario). Refresco casi-instantáneo vía tv_estado. Solo contenido NO sensible.
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import QRCode from 'qrcode';
import { apiClient } from '@/lib/api/client';
import { LOCALE } from '@/lib/utils/regional';

interface TvItem { id: string; nombre: string; descripcion: string | null; precio: number | null; etiqueta: string | null; imagen: string | null }
interface Escena { tipo: string; titulo: string | null; duracion: number; texto?: string | null; imagen?: string | null; items: TvItem[] }
interface TvData { ok: boolean; dispositivo?: string; modo?: 'vitrina' | 'cliente'; escenas?: Escena[] }

const usd = (v: number | null) => (v == null ? '' : Number(v).toLocaleString(LOCALE, { style: 'currency', currency: 'USD' }));
const Placeholder = ({ nombre }: { nombre: string }) => (
  <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-primary/30 to-blue-600/30">
    <span className="px-6 text-center text-3xl font-bold text-white/80">{nombre}</span>
  </div>
);

function QrScene({ titulo, texto }: { titulo: string | null; texto?: string | null }) {
  const [url, setUrl] = useState('');
  useEffect(() => {
    if (texto) QRCode.toDataURL(texto, { width: 520, margin: 2 }).then(setUrl).catch(() => setUrl(''));
    else setUrl('');
  }, [texto]);
  return (
    <div className="flex h-full flex-col items-center justify-center gap-8 p-16 text-center">
      {titulo && <h1 className="text-6xl font-extrabold bg-gradient-to-r from-emerald-300 to-cyan-300 bg-clip-text text-transparent">{titulo}</h1>}
      {url ? <img src={url} alt="QR" className="h-[44vh] w-[44vh] rounded-3xl bg-white p-4 shadow-2xl" /> : <p className="text-white/50">Sin contenido</p>}
      <p className="text-2xl text-white/60">Escanéame con tu celular</p>
    </div>
  );
}

export function TvSignage() {
  const { token } = useParams<{ token: string }>();
  const qc = useQueryClient();

  // Señal ligera (cada 3s): detecta ventas/cambios de plantilla casi al instante
  const { data: estado } = useQuery<{ ok: boolean; modo?: string; sig?: string }>({
    queryKey: ['tvEstado', token],
    queryFn: async () => (await apiClient.post('/rpc/tv_estado', { _token: token })).data,
    refetchInterval: 3000, enabled: !!token,
  });
  // Contenido completo (fallback lento; se refresca cuando cambia la firma)
  const { data } = useQuery<TvData>({
    queryKey: ['tvContenido', token],
    queryFn: async () => (await apiClient.post('/rpc/tv_contenido', { _token: token })).data,
    refetchInterval: 60000, enabled: !!token,
  });
  useEffect(() => { if (estado?.sig) void qc.invalidateQueries({ queryKey: ['tvContenido', token] }); }, [estado?.sig, qc, token]);

  const escenas = data?.escenas ?? [];
  const modo = data?.modo ?? 'vitrina';
  const [sceneIdx, setSceneIdx] = useState(0);
  const [heroIdx, setHeroIdx] = useState(0);
  useEffect(() => { setSceneIdx(0); setHeroIdx(0); }, [modo, escenas.length]);

  const escena = escenas.length ? escenas[sceneIdx % escenas.length] : undefined;
  useEffect(() => {
    if (escenas.length <= 1 || !escena) return;
    const t = setTimeout(() => { setSceneIdx((i) => (i + 1) % escenas.length); setHeroIdx(0); }, (escena.duracion || 15) * 1000);
    return () => clearTimeout(t);
  }, [sceneIdx, escenas.length, escena]);

  const items = escena?.items ?? [];
  useEffect(() => {
    if (items.length <= 1) return;
    const t = setInterval(() => setHeroIdx((i) => (i + 1) % items.length), 6500);
    return () => clearInterval(t);
  }, [items.length, sceneIdx]);

  const hero = items[heroIdx % Math.max(1, items.length)];
  const siguientes = items.length ? Array.from({ length: Math.min(6, items.length) }, (_, k) => items[(heroIdx + 1 + k) % items.length]) : [];

  return (
    <div className="relative h-screen w-screen overflow-hidden bg-neutral-950 text-white cursor-none">
      <div className="absolute inset-0 -z-10 overflow-hidden">
        <motion.div className="absolute -top-40 -left-32 h-[45rem] w-[45rem] rounded-full bg-primary/40 blur-3xl"
          animate={{ x: [0, 160, 0], y: [0, 100, 0], scale: [1, 1.25, 1] }} transition={{ duration: 16, repeat: Infinity, ease: 'easeInOut' }} />
        <motion.div className="absolute bottom-[-12rem] right-[-8rem] h-[42rem] w-[42rem] rounded-full bg-blue-600/40 blur-3xl"
          animate={{ x: [0, -140, 0], y: [0, -90, 0], scale: [1.1, 1, 1.1] }} transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }} />
      </div>

      {data && !data.ok ? (
        <div className="flex h-full items-center justify-center text-2xl text-white/60">Dispositivo no válido o inactivo</div>
      ) : !escena ? (
        <div className="flex h-full items-center justify-center text-2xl text-white/60">Preparando contenido…</div>
      ) : escena.tipo === 'qr' ? (
        <AnimatePresence mode="wait">
          <motion.div key={`qr-${sceneIdx}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.6 }} className="h-full">
            <QrScene titulo={escena.titulo} texto={escena.texto} />
          </motion.div>
        </AnimatePresence>
      ) : escena.tipo === 'banner' ? (
        <AnimatePresence mode="wait">
          <motion.div key={`ban-${sceneIdx}`} initial={{ opacity: 0, scale: 1.03 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.8 }} className="relative h-full w-full">
            {escena.imagen
              ? <motion.img src={escena.imagen} alt={escena.titulo ?? 'banner'} className="h-full w-full object-cover"
                  initial={{ scale: 1 }} animate={{ scale: 1.06 }} transition={{ duration: (escena.duracion || 12), ease: 'linear' }} />
              : <Placeholder nombre={escena.titulo ?? ''} />}
            {(escena.titulo || escena.texto) && (
              <div className="absolute inset-0 flex flex-col justify-end bg-gradient-to-t from-black/70 to-transparent p-16">
                {escena.titulo && <h1 className="text-6xl font-extrabold">{escena.titulo}</h1>}
                {escena.texto && <p className="mt-3 max-w-3xl text-2xl text-white/80">{escena.texto}</p>}
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      ) : escena.tipo === 'mensaje' ? (
        <AnimatePresence mode="wait">
          <motion.div key={`msg-${sceneIdx}`} initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.7 }} className="flex h-full flex-col items-center justify-center p-16 text-center">
            <h1 className="text-7xl font-extrabold leading-tight bg-gradient-to-r from-emerald-300 to-cyan-300 bg-clip-text text-transparent">{escena.titulo}</h1>
            {escena.texto && <p className="mt-8 max-w-4xl text-3xl text-white/70">{escena.texto}</p>}
          </motion.div>
        </AnimatePresence>
      ) : (
        <div className="flex h-full flex-col p-10">
          <div className="flex items-center justify-between">
            <AnimatePresence mode="wait">
              <motion.h1 key={`${sceneIdx}-${escena.titulo}`} initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                className={`text-4xl font-bold tracking-tight ${modo === 'cliente' ? 'text-emerald-300' : 'text-white'}`}>{escena.titulo}</motion.h1>
            </AnimatePresence>
            <span className="text-sm uppercase tracking-widest text-white/40">{data?.dispositivo}</span>
          </div>
          <div className="relative my-6 flex-1">
            <AnimatePresence mode="wait">
              <motion.div key={hero?.id} initial={{ opacity: 0, scale: 1.02 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.8 }}
                className="absolute inset-0 grid grid-cols-1 gap-8 md:grid-cols-2">
                <div className="relative overflow-hidden rounded-3xl ring-1 ring-white/10 shadow-2xl">
                  {hero?.imagen
                    ? <motion.img src={hero.imagen} alt={hero.nombre} className="h-full w-full object-cover" initial={{ scale: 1 }} animate={{ scale: 1.08 }} transition={{ duration: 7, ease: 'easeOut' }} />
                    : <Placeholder nombre={hero?.nombre ?? ''} />}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                </div>
                <div className="flex flex-col justify-center">
                  {hero?.etiqueta && <span className="mb-3 w-fit rounded-full bg-white/10 px-4 py-1 text-sm uppercase tracking-wider text-white/80 backdrop-blur">{hero.etiqueta}</span>}
                  <h2 className="text-6xl font-extrabold leading-tight">{hero?.nombre}</h2>
                  {hero?.descripcion && <p className="mt-4 max-w-xl text-xl text-white/60 line-clamp-3">{hero.descripcion}</p>}
                  {hero?.precio != null && <p className="mt-6 text-5xl font-black bg-gradient-to-r from-emerald-300 to-cyan-300 bg-clip-text text-transparent">{usd(hero.precio)}</p>}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
          <div className="flex gap-4">
            {siguientes.map((it, k) => (
              <motion.div key={`${it.id}-${k}`} layout className="h-28 w-44 shrink-0 overflow-hidden rounded-xl ring-1 ring-white/10 bg-white/5">
                {it.imagen ? <img src={it.imagen} alt={it.nombre} className="h-full w-full object-cover" /> : <Placeholder nombre={it.nombre} />}
              </motion.div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
